/**
 * Hook for current user's allowed roles (permissions)
 */

import { useState, useEffect, useCallback } from "react";
import { getCurrentUserPermissions } from "../services/permissionsService";
import type { UserRole } from "../constants/permissions";

interface UseUserPermissionsResult {
  /** Roles the current user has permission to assume */
  allowedRoles: UserRole[];
  /** Whether permissions are still loading */
  loading: boolean;
  /** Refetch permissions (e.g. after login) */
  refetch: () => Promise<void>;
}

export function useUserPermissions(): UseUserPermissionsResult {
  const [allowedRoles, setAllowedRoles] = useState<UserRole[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchPermissions = useCallback(async () => {
    setLoading(true);
    try {
      const roles = await getCurrentUserPermissions();
      setAllowedRoles(roles);
    } catch {
      setAllowedRoles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchPermissions();
  }, [fetchPermissions]);

  return { allowedRoles, loading, refetch: fetchPermissions };
}
